// eslint-disable-next-line max-classes-per-file

import $ from '../jQuery/jQuery';

class Module {
    constructor(moduleName, moduleClass) {
        this.name = moduleName;
        this.class = moduleClass;
        this.instances = new Map();
    }
}

class ModuleRegistry {
    constructor() {
        this._registeredModules = new Map();
        this._autoApplySelectors = new Map();
        this._addEventListeners();
    }

    _addEventListeners() {
        document.addEventListener('DOMContentLoaded', () => {
            this._initAutoApplyModules();
        });
    }

    registerModule(moduleClassName, moduleClass) {
        if (!moduleClassName) {
            throw new TypeError('moduleClassName must be a non-empty string');
        }

        const isValidModuleClass = ModuleRegistry._validateModuleClass(moduleClass);

        if (!isValidModuleClass) {
            return null;
        }

        const hasModule = this._registeredModules.has(moduleClassName);

        if (hasModule) {
            const existingModule = this._registeredModules.get(moduleClassName);

            if (moduleClass !== existingModule.class) {
                throw new TypeError(`A module with name '${moduleClassName}' is already registered`);
            }

            return existingModule;
        }

        const module = new Module(moduleClassName, moduleClass);

        this._registeredModules.set(moduleClassName, module);

        return module;
    }

    autoApplyModule(moduleClassName, selector) {
        const hasModule = this._registeredModules.has(moduleClassName);

        if (!hasModule) {
            throw new TypeError(`No module with name '${moduleClassName}' was registered`);
        }

        const trimmedSelector = selector ? selector.trim() : selector;

        if (!trimmedSelector) {
            throw new TypeError('selector must be a non-empty string');
        }

        const module = this._registeredModules.get(moduleClassName);

        this._processAutoApplyModule(module, trimmedSelector);
    }

    applyModule(moduleClassName, element) {
        const hasModule = this._registeredModules.has(moduleClassName);

        if (!hasModule) {
            throw new TypeError(`No module with name '${moduleClassName}' was registered`);
        }

        if (!(element instanceof HTMLElement)) {
            throw new TypeError('element must be a HTMLElement');
        }

        const module = this._registeredModules.get(moduleClassName);

        ModuleRegistry._initModule(module, element);
    }

    _processAutoApplyModule(module, selector) {
        if (ModuleRegistry._isDomReady()) {
            const $elements = $(selector);

            $elements.each((i, el) => {
                // init the module immediately if `DOMContentLoaded already fired
                ModuleRegistry._initModule(module, el);
            });
        } else {
            const hasSelector = this._autoApplySelectors.has(selector);
            let modulesForSelector;

            if (hasSelector) {
                modulesForSelector = this._autoApplySelectors.get(selector);
            } else {
                modulesForSelector = new Set();
                this._autoApplySelectors.set(selector, modulesForSelector);
            }

            modulesForSelector.add(module);
        }
    }

    _initAutoApplyModules() {
        this._autoApplySelectors.forEach((modules, selector) => {
            const $elements = $(selector);

            $elements.each((i, el) => {
                modules.forEach((module) => {
                    ModuleRegistry._initModule(module, el);
                });
            });
        });
    }

    static _initModule(module, element) {
        if (module.instances.has(element)) {
            throw new TypeError(`Module '${module.name}' already instantiated on given element`);
        }

        const $element = $(element);
        const elementData = $element.data();

        // eslint-disable-next-line new-cap
        const moduleInstance = new module.class($element, elementData);

        module.instances.set(element, moduleInstance);

        moduleInstance.init();
    }

    static _validateModuleClass(moduleClass) {
        if (!moduleClass || typeof moduleClass !== 'function') {
            throw new TypeError('moduleClass must be a function');
        }

        if (typeof moduleClass.prototype.init !== 'function') {
            throw new TypeError('A module must have a "init" method');
        }

        return true;
    }

    static _isDomReady() {
        const { readyState } = document;
        return readyState === 'interactive' || readyState === 'complete';
    }
}

export default new ModuleRegistry();
